import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useNPVSettingsUpsert } from "@/hooks/useNPVSettingsUpsert";
import { CalcGrid } from "@redux/npv/npv-actions";
import { npvActions } from "@redux/npv/npv-slice";
import { SelectBox, NumberBox, TextBox } from "devextreme-react";
import { useParams } from "react-router";
import type { UPRow, UniquePossibilities } from "@/types/types";
import { calculateAge } from "../UP/up-utils";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useUniquePossibilitiesUpsert } from "@/hooks/useUniquePossibilitiesUpsert";
import { uqActions } from "@/redux/uq/uq-slice";
import CustomButton from "@/components/CustomButton";
import { getInvestmentWithdrawalsSum } from "./npv-utils";
import { formatNumber } from "devextreme/localization";
import { useUpdateYearsToRetire } from "@/hooks/useUpdateYearsToRetire";

type Props = {
  uniquePossibilities: UniquePossibilities;
  setIsMutating: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function TopSettings({ uniquePossibilities, setIsMutating }: Props) {
  const params = useParams();
  const dispatch = useAppDispatch();
  const {
    gridSet,
    isModified,
    settings: npvSettings,
    shouldStopAtAge,
  } = useAppSelector((state) => state.npv);
  const { mutateAsync: saveNPVSettings, isPending } = useNPVSettingsUpsert();
  const {
    gridSet: uqGridSetLocal,
    headerTitles: titles,
    settings: upSettings,
  } = useAppSelector((state) => state.uq);
  const { mutateAsync, isSuccess, isPending: isUpdatingUP } = useUniquePossibilitiesUpsert();
  const queryClient = useQueryClient();
  const { mutateAsync: updateYearsToRetire, isPending: isSavingYears } = useUpdateYearsToRetire();

  useEffect(() => {
    if (isSuccess) {
      dispatch(npvActions.setIsModified(false));
      toast.success("NPV saved successfully!");
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    if (isPending) {
      toast.info("Saving NPV settings...");
      setIsMutating(true);
    }
  }, [isPending, setIsMutating]);

  const household = uniquePossibilities.household;
  const age1 = calculateAge(household?.primaryContact?.birthDate?.toString(), 0)?.value;
  const age2 = calculateAge(household?.secondaryContact?.birthDate.toString(), 0)?.value;
  const ageToUse = age2 ? Math.min(age1, age2) : age1;
  const ageIndex = gridSet.findIndex((gridItem) => gridItem.Age.value === age1);

  const selectedAgeOptions = Array.from(
    { length: shouldStopAtAge - ageToUse - npvSettings?.yearsToRetire - 3 },
    (_, index) => 5 + index
  ).map((i) => (i === 1 ? `${i} year` : `${i} years`));

  const yearsToRetireOptions = Array.from(
    { length: shouldStopAtAge - ageToUse - npvSettings?.npvAge + 2 },
    (_, index) => index
  ).map((i) => (i === 1 ? `${i} year` : `${i} years`));

  const { isNeg: investmentWithdrawalsIsNeg, value: investmentWithdrawalsSum } =
    getInvestmentWithdrawalsSum(gridSet);

  const npvSolution = gridSet[ageIndex + npvSettings?.yearsToRetire]?.NPVSolution?.value ?? 0;

  const saveHandler = async () => {
    await updateYearsToRetire({
      externalCode: params.id as string,
      yearsToRetire: npvSettings?.yearsToRetire,
    });

    saveNPVSettings({
      data: JSON.stringify({
        yearsToRetire: npvSettings?.yearsToRetire,
        npvAge: npvSettings?.npvAge,
        irr: npvSettings?.irr,
      }),
      householdId: params.id,
    }).then(() => {
      dispatch(npvActions.setIsModified(false));

      const gridForUP = gridSet.map((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { NPVSolution, InvestmentWithdrawals, ...rest } = item;
        const matchedRecord = uqGridSetLocal.find((uqItem: UPRow) => uqItem.id === item.id);

        if (matchedRecord) return { ...rest, Retirement: matchedRecord.Retirement };

        return rest;
      });

      mutateAsync({
        data: {
          ...uniquePossibilities,
          data: JSON.stringify({
            gridSet: gridForUP,
            settings: upSettings,
            npvSettings,
            household,
            titles,
          }),
        },
        overwrite: true,
      }).then(() => {
        queryClient.refetchQueries({ queryKey: ["unique-possibilities", params.id!] }).then(() => {
          dispatch(uqActions.setGrid(gridForUP));
          setIsMutating(false);
        });
      });
    });
  };

  return (
    <div className="print-zoom-85 relative top-[6px] flex items-end gap-4 text-neutral-100">
      <div className="flex flex-col gap-5 print:flex-row">
        <SelectBox
          stylingMode="outlined"
          label="NPV for selected years"
          height={51}
          disabled={isPending || isSavingYears || isUpdatingUP}
          value={
            npvSettings?.npvAge === 1
              ? `${npvSettings?.npvAge} year`
              : `${npvSettings?.npvAge} years`
          }
          items={selectedAgeOptions}
          onValueChange={(e) => {
            dispatch(npvActions.setSettings({ ...npvSettings, npvAge: parseInt(e) }));
            dispatch(CalcGrid(uqGridSetLocal));
            dispatch(npvActions.setIsModified(true));
          }}
          width={160}
        />
        <NumberBox
          readOnly
          height={51}
          label="NPV"
          value={npvSolution}
          format={"currency"}
          width={160}
          className="h-12 rounded-md"
        />
      </div>

      <div className="flex flex-col gap-5 print:flex-row">
        <SelectBox
          stylingMode="outlined"
          value={
            npvSettings?.yearsToRetire === 1
              ? `${npvSettings?.yearsToRetire} year`
              : `${npvSettings?.yearsToRetire} years`
          }
          label="Years to retire"
          items={yearsToRetireOptions}
          height={51}
          disabled={isPending || isSavingYears || isUpdatingUP}
          onValueChange={(e) => {
            dispatch(npvActions.setIsModified(true));
            dispatch(
              npvActions.setSettings({
                ...npvSettings,
                yearsToRetire: parseInt(e),
              })
            );
            dispatch(CalcGrid(uqGridSetLocal));
          }}
          width={160}
        />
        <NumberBox
          readOnly
          label="Total fixed income"
          height={51}
          value={household.sgTotalFI ?? 0}
          format={"currency"}
          width={160}
          className="h-12 rounded-md"
        />
      </div>

      <TextBox
        readOnly
        label="Total Withdrawals"
        height={51}
        value={formatNumber(investmentWithdrawalsSum ?? 0, "currency")}
        width={160}
        id="total-withdrawals"
        className={`h-12 rounded-md ${
          investmentWithdrawalsIsNeg ? "text-danger" : "text-neutral-100"
        }`}
      />

      <div className="ml-auto flex gap-4">
        {isModified ? (
          <CustomButton
            text="Save"
            disabled={isPending || isSavingYears || isUpdatingUP}
            onClickHandler={saveHandler}
            variant="primary"
          />
        ) : null}
      </div>
    </div>
  );
}
